import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useI18n } from '@/contexts/I18nContext';
import { CURRENCY_SYMBOLS, CURRENCY_LABELS, type Currency } from '@/lib/i18n';
import { Coins } from 'lucide-react';

const ALL_CURRENCIES = Object.keys(CURRENCY_SYMBOLS) as Currency[];

interface CurrencySwitcherProps {
  size?: 'sm' | 'icon' | 'default';
  variant?: 'ghost' | 'outline';
  className?: string;
}

export default function CurrencySwitcher({
  size = 'sm',
  variant = 'ghost',
  className,
}: CurrencySwitcherProps) {
  const { displayCurrency, setDisplayCurrency, rateDate, t } = useI18n();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="currency-switcher"
          variant={variant}
          size={size}
          className={`text-muted-foreground hover:text-primary font-medium gap-1.5 px-2 ${className ?? ''}`}
          aria-label={t('currency.aria_label' as any)}
        >
          <Coins className="w-3.5 h-3.5" />
          <span className="text-xs font-semibold">{displayCurrency}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto w-56">
        <DropdownMenuLabel className="text-xs">
          {t('currency.title' as any)}
        </DropdownMenuLabel>
        {rateDate && (
          <p className="px-2 pb-1 text-[10px] text-muted-foreground">
            {(t('currency.rates_of' as any) || 'Rates of {date}').replace('{date}', rateDate)}
          </p>
        )}
        <DropdownMenuSeparator />
        {ALL_CURRENCIES.map((cur) => (
          <DropdownMenuItem
            key={cur}
            onClick={() => setDisplayCurrency(cur)}
            className={displayCurrency === cur ? 'bg-primary/10 text-primary font-medium' : ''}
          >
            <span className="mr-2 w-6 inline-block text-center">{CURRENCY_SYMBOLS[cur]}</span>
            <span className="text-sm">{CURRENCY_LABELS[cur]}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
